import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "./DigitalMarketing.css";
import ScrollToTop from "../components/ScrollToTop";
import { useNavigate } from "react-router-dom";

import digital1 from "../assets/digital1.jpg";
import digital2 from "../assets/digital2.jpg";
import digital3 from "../assets/digital3.jpeg";
import digitalHero from "../assets/digital-hero.png";

const AUTOPLAY_MS = 4500;

const DigitalMarketing = () => {
  const navigate = useNavigate();

  const baseSlides = useMemo(
    () => [
      {
        title: "Unified Digital Strategy",
        text: "Our digital marketing services provide a comprehensive solution for businesses looking to grow their online presence and reach targeted audiences. We integrate multiple digital channels including SEO, paid advertising, social media marketing, email marketing, and content marketing into a unified strategy.",
        image: digital1,
      },
      {
        title: "Meaningful Digital Experiences",
        text: "Our approach focuses on creating meaningful digital experiences through strategic planning, creative execution, and continuous optimization. By combining audience insights with innovative digital techniques, we help businesses strengthen brand identity, drive qualified traffic, and improve conversion rates across digital platforms.",
        image: digital2,
      },
      {
        title: "Track, Measure, Optimize",
        text: "By leveraging tools such as search engine optimization (SEO), social media marketing, email campaigns, content marketing, and paid advertising, digital marketing helps organizations increase visibility, generate leads, and build strong customer relationships. Advanced analytics and performance tracking allow businesses to measure results, understand customer behavior, and continuously optimize their strategies.",
        image: digital3,
      },
    ],
    []
  );

  // Cloned slides: [lastClone, ...real, firstClone]
  const slides = useMemo(() => {
    if (!baseSlides.length) return [];
    const first = baseSlides[0];
    const last = baseSlides[baseSlides.length - 1];
    return [last, ...baseSlides, first];
  }, [baseSlides]);

  const totalReal = baseSlides.length;

  // Start at 1 (first real slide)
  const [index, setIndex] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(true);

  const autoplayRef = useRef(null);
  const carouselRef = useRef(null);

  // track visibility + hover state
  const isVisibleRef = useRef(false);
  const isHoveringRef = useRef(false);

  const stopAutoplay = useCallback(() => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
      autoplayRef.current = null;
    }
  }, []);

  const resetCarousel = useCallback(() => {
    setIsTransitioning(false);
    setIndex(1);
    requestAnimationFrame(() => setIsTransitioning(true));
  }, []);

  // safe autoplay (prevents runaway index if transitionEnd doesn't fire)
  const startAutoplay = useCallback(() => {
    stopAutoplay();

    autoplayRef.current = setInterval(() => {
      setIsTransitioning(true);
      setIndex((prev) => {
        const next = prev + 1;
        if (next > totalReal + 1) return 1;
        return next;
      });
    }, AUTOPLAY_MS);
  }, [stopAutoplay, totalReal]);

  // backup snap logic even if transitionEnd doesn't fire
  useEffect(() => {
    if (!totalReal) return;

    if (index === totalReal + 1) {
      const t = requestAnimationFrame(() => {
        setIsTransitioning(false);
        setIndex(1);
      });
      return () => cancelAnimationFrame(t);
    }

    if (index === 0) {
      const t = requestAnimationFrame(() => {
        setIsTransitioning(false);
        setIndex(totalReal);
      });
      return () => cancelAnimationFrame(t);
    }
  }, [index, totalReal]);

  // Re-enable animation after snap
  useEffect(() => {
    if (!isTransitioning) {
      const t = requestAnimationFrame(() => setIsTransitioning(true));
      return () => cancelAnimationFrame(t);
    }
  }, [isTransitioning]);

  // pause/resume based on visibility
  useEffect(() => {
    if (!carouselRef.current) return;

    const el = carouselRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        if (entry.isIntersecting) {
          isVisibleRef.current = true;

          resetCarousel();
          if (!isHoveringRef.current) startAutoplay();
        } else {
          isVisibleRef.current = false;
          stopAutoplay();
        }
      },
      { threshold: 0.15 }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
      stopAutoplay();
    };
  }, [resetCarousel, startAutoplay, stopAutoplay]);

  // pause on hover
  const handleMouseEnter = () => {
    isHoveringRef.current = true;
    stopAutoplay();
  };

  const handleMouseLeave = () => {
    isHoveringRef.current = false;
    if (isVisibleRef.current) startAutoplay();
  };

  // Optional: pause on keyboard focus too
  const handleFocus = () => {
    isHoveringRef.current = true;
    stopAutoplay();
  };

  const handleBlur = () => {
    isHoveringRef.current = false;
    if (isVisibleRef.current) startAutoplay();
  };

  const onTransitionEnd = () => {
    if (index === totalReal + 1) {
      setIsTransitioning(false);
      setIndex(1);
      return;
    }
    if (index === 0) {
      setIsTransitioning(false);
      setIndex(totalReal);
      return;
    }
  };

  // ✅ UPDATED: Learn More navigates to contact page
  const handleLearnMore = () => {
    navigate("/contact");
  };

  return (
    <section className="service-page digital-marketing-page">
      <ScrollToTop />

      {/* ================= HERO SECTION (FULL SCREEN) ================= */}
      <div className="dm-hero">
        <div className="dm-hero-inner">
          <div className="dm-hero-content">
            <h1 className="dm-hero-title">
              <span className="dm-highlight">Digital Marketing</span> That Boosts
              Visibility & Conversions
            </h1>

            <p className="dm-hero-description">
              We build integrated digital marketing strategies that combine SEO,
              paid advertising, social media, content, and email campaigns to
              attract the right audience. Our focus is on measurable growth 
              more qualified traffic, stronger engagement, and higher
              conversions across your digital channels.
            </p>

            <button className="dm-hero-btn" onClick={handleLearnMore}>
              Get In Touch
            </button>
          </div>

          <div className="dm-hero-image">
            <img src={digitalHero} alt="Digital Marketing Hero" />
          </div>
        </div>
      </div>

      {/* ================= CAROUSEL (HEADER REMOVED) ================= */}
      <div className="service-container">
        <div
          className="dm-carousel"
          id="dm-carousel"
          ref={carouselRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onFocus={handleFocus}
          onBlur={handleBlur}
          tabIndex={0}
        >
          <div className="dm-viewport">
            <div
              className={`dm-track ${
                isTransitioning ? "is-animating" : "no-anim"
              }`}
              style={{ transform: `translateX(-${index * 100}%)` }}
              onTransitionEnd={onTransitionEnd}
            >
              {slides.map((slide, i) => (
                <article className="dm-slide" key={`${slide.title}-${i}`}>
                  <div className="dm-left">
                    <h2 className="dm-slide-title">{slide.title}</h2>
                    <p className="dm-slide-text">{slide.text}</p>
                  </div>

                  <div className="dm-right">
                    <div className="dm-image-wrap">
                      <img
                        src={slide.image}
                        alt={slide.title}
                        className="dm-image"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DigitalMarketing;
